import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Edit } from "@syncfusion/ej2-react-grids";
import { vehicleGroupService } from "../../Services/vehicle.group.service";
import { Toast } from "react-bootstrap";
import { Filters } from "../../components/Filters";
import { HiOutlineRectangleGroup } from "react-icons/hi2";
import { usersService } from "../../Services/users.service";
import { useDispatch } from "react-redux";

export const GroupUsers = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch({ type: "title", value: t("GROUP_USERS.TITLE") });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);
    const gridRef = useRef(null);
    const [bindList, setBindList] = useState([]);
    const [, setSelectedVehicleIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [vehicleGroups, setVehicleGroups] = useState(null)
    const [selectedGroupId, setSelectedGroupId] = useState(null);

    const [selectedBindMarker, setSelectedBindMarker] = useState(null);
    const [originalBindList, setOriginalBindList] = useState([]);

    const [toastConfig, setToastConfig] = useState({
        show: false,
        title: "",
        body: "",
        color: "Success",
    });

    // Busca apenas os grupos ao montar o componente
    useEffect(() => {
        fetchVehicles();
        fetchSelectedMarker();
    }, []);

    const fetchSelectedMarker = async () => {
        try {
            const marker = {
                markerId: 123,
                markerTypeId: 1,
                markerName: "Ponto de Controle X"
            };
            setSelectedBindMarker(marker);
        } catch (error) {
            console.error("Erro ao buscar marcador:", error);
        }
    };

    // Função para buscar apenas os grupos
    const fetchVehicles = async () => {
        try {
            const response = await vehicleGroupService.fetchGroups();
            if (response) {
                setVehicleGroups(response);
            }
        } catch (error) {
            console.error("Erro ao buscar grupos:", error);
            setVehicleGroups([]);
        }
    };

    // Função para buscar usuários do grupo selecionado
    const fetchUsersByGroup = async (groupId) => {
        setLoadingData(true);
        try {
            const response = await usersService.fetchUsersByGroup(groupId);
            if (response) {
                setBindList(response);
                setOriginalBindList(response); // snapshot inicial
                setSelectedVehicleIds(
                    response.filter(u => u.linked === "S").map(u => u.userId)
                );
            } else {
                setBindList([]);
                setOriginalBindList([]);
                setSelectedVehicleIds([]);
            }
        } catch (error) {
            console.error("Erro ao buscar usuários do grupo:", error);
            setBindList([]);
            setOriginalBindList([]);
            setSelectedVehicleIds([]);
        } finally {
            setLoadingData(false);
        }
    };


    // helper pra atualizar a linha dentro de bindList
    const toggleUserLinked = (userId, newLinked) => {
        setBindList(prev =>
            prev.map(item =>
                item.userId === userId ? { ...item, linked: newLinked } : item
            )
        );
    };


    // Template para checkbox de vinculação
    const bindCheckboxTemplate = (props) => {
        const isChecked = props.linked === "S";

        const handleChange = (e) => {
            const checked = e.target.checked;
            const newLinked = checked ? "S" : "N";

            // atualiza linha no grid
            toggleUserLinked(props.userId, newLinked);

            if (checked) {
                setSelectedVehicleIds(prev =>
                    prev.includes(props.userId) ? prev : [...prev, props.userId]
                );
            } else {
                setSelectedVehicleIds(prev =>
                    prev.filter(id => id !== props.userId)
                );
            }
        };

        return (
            <div style={{ display: "flex", justifyContent: "center" }}>
                <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleChange}
                />
            </div>
        );
    };


    // Ao selecionar um grupo, carrega os usuários desse grupo
    const handleGroupChange = (selectedValue) => {
        console.log("Grupo selecionado:", selectedValue);

        setSelectedGroupId(selectedValue);
        setSelectedVehicleIds([]); // Limpa seleções anteriores

        // ✅ Carrega os usuários do grupo selecionado
        if (selectedValue && selectedValue !== "0" && selectedValue !== "") {
            fetchUsersByGroup(selectedValue);
        } else {
            setBindList([]); // Se desmarcou ou selecionou "0", limpa a lista
        }
    };

    const handleSelectAll = () => {
        const allIds = bindList.map((item) => item.userId);

        // marca todos como "S"
        setBindList(prev =>
            prev.map(item => ({ ...item, linked: "S" }))
        );

        setSelectedVehicleIds(allIds);
    };

    const handleRemoveAll = () => {
        setBindList(prev =>
            prev.map(item => ({ ...item, linked: "N" }))
        );
        setSelectedVehicleIds([]);
    };


    // Função para mostrar toast
    const showToast = (title, body, color = "Success") => {
        setToastConfig({
            show: true,
            title,
            body,
            color,
        });
    };

    // Função de salvamento completa
    const handleSave = async () => {
        if (!selectedBindMarker) {
            showToast(t("COMMON.ATTENTION"), t("GROUP_USERS.NO_MARKER_SELECTED"), "Warning");
            return;
        }

        if (!selectedGroupId) {
            showToast(t("COMMON.ATTENTION"), t("GROUP_USERS.NO_GROUP_SELECTED"), "Warning");
            return;
        }

        setLoading(true);
        try {
            // mapa do estado original
            const originalMap = new Map(
                originalBindList.map(u => [u.userId, u.linked]) // "S" ou "N"
            );

            const usersToBind = [];
            const usersToUnbind = [];

            // compara original x atual
            bindList.forEach(user => {
                const originalLinked = originalMap.get(user.userId); // "S" ou "N"
                const currentLinked = user.linked;                   // "S" ou "N" atual

                if (originalLinked === "N" && currentLinked === "S") {
                    usersToBind.push(user.userId);
                }
                if (originalLinked === "S" && currentLinked === "N") {
                    usersToUnbind.push(user.userId);
                }
            });

            const promises = [];

            usersToBind.forEach(userId => {
                const bindPayload = {
                    userId,
                    vehicleGroupId: Number(selectedGroupId),
                };
                promises.push(vehicleGroupService.bindUsersGroups(bindPayload));
            });

            usersToUnbind.forEach(userId => {
                const unbindPayload = {
                    userId,
                    vehicleGroupId: Number(selectedGroupId),
                };
                promises.push(vehicleGroupService.unbindUsersGroups(unbindPayload));
            });

            const responses = await Promise.all(promises);
            const hasError = responses.some(res => !res || res.error);

            if (!hasError) {
                const totalChanges = usersToBind.length + usersToUnbind.length;
                showToast(
                    t("COMMON.SUCCESS"),
                    `${totalChanges} ${t("GROUP_USERS.CHANGES_SAVED")}`,
                    "Success"
                );

                setSelectedVehicleIds([]);
                await fetchUsersByGroup(selectedGroupId); // ressincroniza com backend
            } else {
                const body =
                    responses.find(r => r?.message)?.message ||
                    responses.find(r => r?.friendlyMessage)?.friendlyMessage ||
                    t("GROUP_USERS.SAVE_ERROR");
                showToast(t("COMMON.ERROR"), body, "Danger");
            }
        } catch (error) {
            console.error("Erro ao salvar vinculações:", error);
            showToast(t("COMMON.ERROR"), t("GROUP_USERS.REQUEST_ERROR"), "Danger");
        } finally {
            setLoading(false);
        }
    };


    return (
        <div style={{ padding: "20px" }}>
            {/* Toast de notificação */}
            <Toast
                show={toastConfig.show}
                onClose={() => setToastConfig({ ...toastConfig, show: false })}
                delay={3000}
                autohide
                style={{
                    position: "fixed",
                    top: 20,
                    right: 20,
                    minWidth: 250,
                    zIndex: 9999,
                }}
                bg={toastConfig.color.toLowerCase()}
            >
                <Toast.Header>
                    <strong className="me-auto">{toastConfig.title}</strong>
                </Toast.Header>
                <Toast.Body className={toastConfig.color === "Success" ? "text-white" : ""}>
                    {toastConfig.body}
                </Toast.Body>
            </Toast>

            {/* Header */}
            <div style={{ marginBottom: "20px" }}>
                <div
                    style={{
                        display: "flex",
                        gap: 8,
                        marginBottom: 20,
                    }}
                >
                    <Filters
                        id={"gruposModal"}
                        isMulti={false}
                        title={t("GROUP_USERS.GROUPS")}
                        pageTitle={"groups-user"}
                        options={vehicleGroups || []}
                        icon={HiOutlineRectangleGroup}
                        hasIcon={true}
                        onChange={handleGroupChange}
                    />
                    <Button
                        variant="primary"
                        onClick={handleSelectAll}
                        disabled={loadingData || bindList.length === 0}
                    >
                        {t("GROUP_USERS.SELECT_ALL")}
                    </Button>

                    <Button
                        variant="secondary"
                        onClick={handleRemoveAll}
                        disabled={loadingData}
                    >
                        {t("GROUP_USERS.REMOVE_ALL")}
                    </Button>
                </div>
            </div>

            {/* Body */}
            <div style={{ marginBottom: "20px" }}>
                {loadingData && <p>{t("GROUP_USERS.LOADING_USERS")}</p>}
                {loading && <p>{t("GROUP_USERS.PROCESSING")}</p>}

                {!loadingData && bindList.length === 0 && !selectedGroupId ? (
                    <p
                        style={{
                            textAlign: "center",
                            fontWeight: "500",
                            padding: "20px 0",
                        }}
                    >
                        {t("GROUP_USERS.SELECT_GROUP_MESSAGE")}
                    </p>
                ) : !loadingData && bindList.length === 0 ? (
                    <p
                        style={{
                            textAlign: "center",
                            fontWeight: "500",
                            padding: "20px 0",
                        }}
                    >
                        {t("GROUP_USERS.NO_USERS_FOUND")}
                    </p>
                ) : !loadingData && bindList.length > 0 ? (
                    <GridComponent
                        ref={gridRef}
                        dataSource={bindList}
                        allowPaging={false}
                        cssClass="custom-grid-control"
                        selectionSettings={{ type: "Multiple" }}
                    >
                        <ColumnsDirective>
                            <ColumnDirective
                                field="userName"
                                headerText={t("GROUP_USERS.USER")}
                                width="100"
                                textAlign="Left"
                            />
                            <ColumnDirective
                                field="email"
                                headerText={t("GROUP_USERS.EMAIL")}
                                width="100"
                                textAlign="Left"
                            />
                            <ColumnDirective
                                headerText={t("GROUP_USERS.BIND")}
                                width="100"
                                textAlign="Center"
                                template={bindCheckboxTemplate}
                            />
                        </ColumnsDirective>
                        <Inject services={[Edit]} />
                    </GridComponent>
                ) : null}
            </div>

            {/* Footer */}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={loading || loadingData || !selectedBindMarker || !selectedGroupId}
                >
                    {loading ? t("GROUP_USERS.SAVING") : t("GROUP_USERS.SAVE")}
                </Button>
            </div>
        </div>
    );
};
